// frontend/src/components/modals/AchievementDetailModal.jsx
// Achievement detail modal with progress and requirements

import React from 'react';
import { FiAward, FiX, FiTarget, FiCalendar } from 'react-icons/fi';

export default function AchievementDetailModal({ achievement, onClose }) {
  if (!achievement) return null;

  const getProgressPercentage = () => {
    if (!achievement.required || achievement.earned) return 100;
    return Math.round((achievement.current / achievement.required) * 100);
  };

  const getTimeToComplete = () => {
    if (achievement.earned) return null;
    if (!achievement.current || !achievement.required) return 'Unknown';
    
    const remaining = achievement.required - achievement.current;
    const rate = achievement.weekly_rate || 1; // Assume 1 per week if not specified
    const weeks = Math.ceil(remaining / rate);
    
    if (weeks <= 1) return 'Less than a week';
    if (weeks <= 4) return `${weeks} weeks`;
    return `${Math.ceil(weeks / 4)} months`;
  };

  return (
    <div className="modal-backdrop" onClick={onClose}>
      <div className="modal-content achievement-detail-modal" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h3>Achievement Details</h3>
          <button className="modal-close" onClick={onClose}>
            <FiX size={20} />
          </button>
        </div>
        
        <div className="modal-body">
          <div className="achievement-hero">
            <div className={`achievement-icon-large ${achievement.earned ? 'earned' : 'locked'}`}>
              <FiAward size={64} />
            </div>
            
            <div className="achievement-info">
              <h2>{achievement.name}</h2>
              <p className="achievement-description">{achievement.description}</p>
              
              {achievement.earned ? (
                <div className="earned-badge">
                  <FiCalendar size={16} />
                  Earned on {new Date(achievement.date_earned).toLocaleDateString()}
                </div>
              ) : (
                <div className="progress-info">
                  <div className="progress-bar-large">
                    <div 
                      className="progress-fill" 
                      style={{ width: `${getProgressPercentage()}%` }}
                    ></div>
                  </div>
                  <div className="progress-text">
                    {achievement.current || 0} / {achievement.required || 1}
                  </div>
                </div>
              )}
            </div>
          </div>
          
          {!achievement.earned && (
            <div className="achievement-requirements">
              <h4>Requirements</h4>
              <div className="requirements-list">
                <div className="requirement-item">
                  <FiTarget size={16} />
                  <span>{achievement.requirement_description || achievement.description}</span>
                </div>
                
                {achievement.additional_requirements?.map((req, index) => (
                  <div key={index} className="requirement-item">
                    <FiTarget size={16} />
                    <span>{req}</span>
                  </div>
                ))}
              </div>
              
              {getTimeToComplete() && (
                <div className="time-estimate">
                  <h5>Estimated Time to Complete</h5>
                  <p>{getTimeToComplete()}</p>
                </div>
              )}
            </div>
          )}
          
          <div className="achievement-stats">
            <h4>Achievement Stats</h4>
            <div className="stats-grid">
              <div className="stat-item">
                <span className="stat-label">Difficulty</span>
                <span className="stat-value">{achievement.difficulty || 'Medium'}</span>
              </div>
              <div className="stat-item">
                <span className="stat-label">Category</span>
                <span className="stat-value">{achievement.category || 'General'}</span>
              </div>
              <div className="stat-item">
                <span className="stat-label">Points</span>
                <span className="stat-value">{achievement.points || 100}</span>
              </div>
              {achievement.rarity && (
                <div className="stat-item">
                  <span className="stat-label">Rarity</span>
                  <span className="stat-value">{achievement.rarity}</span>
                </div>
              )}
            </div>
          </div>
          
          {achievement.tips && (
            <div className="achievement-tips">
              <h4>Tips</h4>
              <ul>
                {achievement.tips.map((tip, index) => (
                  <li key={index}>{tip}</li>
                ))}
              </ul>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}